import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import { PageHeader, SectionCard } from '../components/ui';
import { useToast, ToastContainer } from '../components/ui';
import { Bell, CreditCard, Shield, Globe, User, Lock } from 'lucide-react';

interface NotifPrefs {
  orderAlerts: boolean;
  payoutAlerts: boolean;
  inventoryAlerts: boolean;
}

const NOTIF_STORAGE_KEY = 'creatorhub360_vendor_notif_prefs';

function loadNotifPrefs(): NotifPrefs {
  try {
    const raw = localStorage.getItem(NOTIF_STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return { orderAlerts: true, payoutAlerts: true, inventoryAlerts: true };
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button type="button" onClick={() => onChange(!checked)}
      className="relative w-9 h-5 rounded-full transition-colors focus:outline-none"
      style={{ backgroundColor: checked ? '#FFD400' : 'rgba(255,255,255,0.12)' }}>
      <span className="absolute top-0.5 transition-transform w-4 h-4 rounded-full"
        style={{
          backgroundColor: checked ? '#0B0B0B' : 'rgba(255,255,255,0.4)',
          transform: checked ? 'translateX(18px)' : 'translateX(2px)',
        }} />
    </button>
  );
}

export default function VendorSettings() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation(['vendor', 'common']);
  const { toasts, addToast, removeToast } = useToast();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwLoading, setPwLoading] = useState(false);
  const [notifPrefs, setNotifPrefs] = useState<NotifPrefs>(loadNotifPrefs);

  useEffect(() => {
    localStorage.setItem(NOTIF_STORAGE_KEY, JSON.stringify(notifPrefs));
  }, [notifPrefs]);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 8) { addToast(t('vendor:settings.passwordMinLength', 'Password must be at least 8 characters'), 'error'); return; }
    if (newPassword !== confirmPassword) { addToast(t('vendor:settings.passwordMismatch', 'Passwords do not match'), 'error'); return; }
    setPwLoading(true);
    try {
      await api.changePassword(currentPassword, newPassword);
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
      addToast(t('common:success'), 'success');
    } catch (err) {
      addToast(err instanceof Error ? err.message : t('common:error'), 'error');
    } finally { setPwLoading(false); }
  };

  const handleLangChange = async (locale: string) => {
    try {
      await api.updateVendorLocale(locale);
      i18n.changeLanguage(locale);
    } catch { addToast(t('common:error'), 'error'); }
  };

  const updateNotif = (key: keyof NotifPrefs) => {
    setNotifPrefs(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const sectionIcon = (Icon: typeof User) => <Icon className="w-4 h-4 text-[#FFD400]" />;

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <PageHeader title={t('vendor:settings.title')} />

      {/* Profile / Account */}
      <SectionCard>
        <div className="flex items-center gap-2 mb-4">
          {sectionIcon(User)}
          <h3 className="text-sm font-semibold text-white">{t('vendor:settings.account')}</h3>
        </div>
        <div className="space-y-2">
          <div>
            <span className="text-[11px] text-white/40 uppercase tracking-wider">{t('common:email')}</span>
            <p className="text-sm text-white/70">{user?.email}</p>
          </div>
          <div>
            <span className="text-[11px] text-white/40 uppercase tracking-wider">{t('vendor:settings.role')}</span>
            <p className="text-sm text-white/70 capitalize">{user?.role}</p>
          </div>
        </div>
      </SectionCard>

      {/* Language */}
      <SectionCard>
        <div className="flex items-center gap-2 mb-4">
          {sectionIcon(Globe)}
          <h3 className="text-sm font-semibold text-white">{t('vendor:settings.languageSection')}</h3>
        </div>
        <div className="flex gap-2">
          {['en', 'zh'].map(lng => (
            <button key={lng} onClick={() => handleLangChange(lng)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${i18n.language === lng ? 'text-[#0B0B0B]' : 'text-white/50'}`}
              style={{
                backgroundColor: i18n.language === lng ? '#FFD400' : 'rgba(255,255,255,0.04)',
                border: `1px solid ${i18n.language === lng ? '#FFD400' : 'rgba(255,255,255,0.08)'}`,
                borderRadius: '2px',
              }}>
              {lng === 'en' ? t('common:english') : t('common:chinese')}
            </button>
          ))}
        </div>
      </SectionCard>

      {/* Notifications */}
      <SectionCard>
        <div className="flex items-center gap-2 mb-4">
          {sectionIcon(Bell)}
          <h3 className="text-sm font-semibold text-white">{t('vendor:settings.notifications')}</h3>
        </div>
        <div className="space-y-4">
          {([
            { key: 'orderAlerts' as const, label: t('vendor:settings.notifOrderAlerts'), desc: t('vendor:settings.notifOrderAlertsDesc') },
            { key: 'payoutAlerts' as const, label: t('vendor:settings.notifPayoutAlerts'), desc: t('vendor:settings.notifPayoutAlertsDesc') },
            { key: 'inventoryAlerts' as const, label: t('vendor:settings.notifInventoryAlerts'), desc: t('vendor:settings.notifInventoryAlertsDesc') },
          ]).map(n => (
            <div key={n.key} className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white/80">{n.label}</p>
                <p className="text-xs text-white/35">{n.desc}</p>
              </div>
              <Toggle checked={notifPrefs[n.key]} onChange={() => updateNotif(n.key)} />
            </div>
          ))}
        </div>
      </SectionCard>

      {/* Change password */}
      <SectionCard>
        <div className="flex items-center gap-2 mb-4">
          {sectionIcon(Lock)}
          <h3 className="text-sm font-semibold text-white">{t('common:changePassword')}</h3>
        </div>
        <form onSubmit={handleChangePassword} className="space-y-4 max-w-sm">
          <div className="space-y-1.5">
            <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider">{t('common:currentPassword')}</label>
            <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className="input" required />
          </div>
          <div className="space-y-1.5">
            <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider">{t('common:newPassword')}</label>
            <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="input" required />
          </div>
          <div className="space-y-1.5">
            <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider">{t('common:confirmPassword')}</label>
            <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="input" required />
          </div>
          <button type="submit" disabled={pwLoading} className="btn-primary">
            {pwLoading ? '...' : t('common:changePassword')}
          </button>
        </form>
      </SectionCard>

      {/* Payout Settings */}
      <SectionCard>
        <div className="flex items-center gap-2 mb-4">
          {sectionIcon(CreditCard)}
          <h3 className="text-sm font-semibold text-white">{t('vendor:settings.payoutSettings')}</h3>
        </div>
        <div className="p-4" style={{ backgroundColor: 'rgba(255,255,255,0.015)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '2px' }}>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm" style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.35)' }}>
              {t('common:comingSoon')}
            </span>
          </div>
          <p className="text-sm text-white/35">{t('vendor:settings.payoutMethodPlaceholder')}</p>
        </div>
      </SectionCard>

      {/* Security */}
      <SectionCard>
        <div className="flex items-center gap-2 mb-4">
          {sectionIcon(Shield)}
          <h3 className="text-sm font-semibold text-white">{t('vendor:settings.security')}</h3>
        </div>
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm text-white/55">{t('vendor:settings.lastLogin')}</span>
            <span className="text-sm text-white/70">{t('vendor:settings.currentSession')}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-white/55">{t('vendor:settings.activeSessions')}</span>
            <span className="text-sm text-white/70">1</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-white/55">{t('vendor:settings.twoFactor')}</span>
            <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm"
              style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.35)' }}>
              {t('vendor:settings.twoFactorStatus')}
            </span>
          </div>
        </div>
      </SectionCard>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
}
